import { Fragment } from 'react';

/// The public feature page, served at /features.
///
/// Static by design: it makes no API call and needs no token, so it renders
/// before the sign-in gate in `App`. The distribution already rewrites unknown
/// paths to index.html, which is what makes a real path work rather than a hash.
///
/// Written for someone deciding whether they want it, not for someone
/// maintaining it: every line states what the product does, never how it is
/// built. The register is deliberately closer to a product page than to the
/// rest of the app's copy, which talks to an owner rather than a reader.

type Support = 'full' | 'part' | 'none';

const MARK: Record<Support, string> = { full: '●', part: '◐', none: '–' };
const LABEL: Record<Support, string> = { full: 'Yes', part: 'Partly', none: 'No' };

interface Feature {
  name: string;
  note?: number;
  /** iPhone, Watch, Mac, Web. */
  on: [Support, Support, Support, Support];
}

const PLATFORMS = ['iPhone', 'Watch', 'Mac', 'Web'] as const;

const GROUPS: Array<{ title: string; blurb: string; features: Feature[] }> = [
  {
    title: 'Progress and performance',
    blurb: 'The figures that define the walk, recalculated continuously.',
    features: [
      { name: 'Distance covered today', on: ['full', 'full', 'full', 'full'] },
      { name: 'Remaining distance to the daily target', on: ['full', 'full', 'full', 'full'] },
      { name: 'Live pace while walking', on: ['full', 'full', 'full', 'full'] },
      { name: 'Position against the plan', on: ['full', 'full', 'full', 'full'] },
      { name: 'Daily ranking against your full history', on: ['full', 'full', 'full', 'full'] },
      { name: 'Consecutive days on target', on: ['full', 'full', 'full', 'full'] },
      { name: 'Projected completion date at current pace', on: ['full', 'full', 'full', 'full'] },
      { name: 'Custom checkpoints', note: 1, on: ['full', 'part', 'full', 'full'] },
    ],
  },
  {
    title: 'History and analysis',
    blurb: 'A complete record of every day, and the tools to interrogate it.',
    features: [
      { name: 'Full daily history with distance, steps and rank', on: ['full', 'none', 'full', 'full'] },
      { name: 'Interactive charts with date filtering', note: 2, on: ['part', 'none', 'part', 'full'] },
      { name: 'Conversion between miles, steps and minutes', note: 3, on: ['none', 'none', 'full', 'none'] },
      { name: 'Create and configure walks', on: ['full', 'none', 'full', 'full'] },
    ],
  },
  {
    title: 'Route planning',
    blurb: 'Define the journey once, then follow it on every device.',
    features: [
      { name: 'Plot a route between any two locations', on: ['none', 'none', 'none', 'full'] },
      { name: 'Refine the line by dragging or adding waypoints', on: ['none', 'none', 'none', 'full'] },
      { name: 'Import GPX, KML or GeoJSON', note: 8, on: ['none', 'none', 'none', 'full'] },
      { name: 'Rebuild a route from a Google Maps link', note: 9, on: ['none', 'none', 'none', 'full'] },
      { name: 'Save and reuse routes', on: ['none', 'none', 'none', 'full'] },
      { name: 'Start a walk from a saved route', note: 10, on: ['full', 'none', 'full', 'full'] },
      { name: 'Apply a route to a walk already in progress', on: ['full', 'none', 'full', 'full'] },
      { name: 'Live position on the map', on: ['full', 'none', 'full', 'full'] },
      { name: 'Street View from your current position', on: ['none', 'none', 'none', 'full'] },
    ],
  },
  {
    title: 'Step capture',
    blurb: 'Automatic collection from the devices you already carry.',
    features: [
      { name: 'Automatic step capture', on: ['full', 'full', 'none', 'none'] },
      { name: 'Continues with the app closed', on: ['full', 'full', 'none', 'none'] },
      { name: 'Live capture from Apple Watch during a session', note: 4, on: ['none', 'full', 'none', 'none'] },
      { name: 'Offline capture with guaranteed upload', on: ['full', 'none', 'none', 'none'] },
      { name: 'Manual entry and correction', note: 5, on: ['none', 'none', 'full', 'full'] },
    ],
  },
  {
    title: 'At a glance',
    blurb: 'Your position surfaced without opening anything.',
    features: [
      { name: 'Home screen widgets in three sizes', on: ['full', 'none', 'none', 'none'] },
      { name: 'Lock Screen widgets', on: ['full', 'none', 'none', 'none'] },
      { name: 'Watch face complications', on: ['none', 'full', 'none', 'none'] },
      { name: 'Live Activity during a session', on: ['full', 'none', 'none', 'none'] },
      { name: 'Mac menu bar item with quick entry', on: ['none', 'none', 'full', 'none'] },
      { name: 'Siri shortcuts', on: ['full', 'none', 'none', 'none'] },
      { name: 'Reminders when the day is falling behind', note: 6, on: ['full', 'part', 'none', 'none'] },
      { name: 'Prompt to close a finished session', on: ['full', 'full', 'none', 'none'] },
    ],
  },
  {
    title: 'Account and sharing',
    blurb: 'One sign-in, consistent data, and control over what others see.',
    features: [
      { name: 'Single sign-in per device', note: 7, on: ['full', 'none', 'full', 'full'] },
      { name: 'Sign in with Google', on: ['full', 'none', 'full', 'full'] },
      { name: 'Continuous refresh while open', on: ['full', 'full', 'full', 'full'] },
      { name: 'Launches at login on Mac', on: ['none', 'none', 'full', 'none'] },
      { name: 'Shareable read-only progress link', note: 11, on: ['none', 'none', 'none', 'full'] },
    ],
  },
];

const NOTES: string[] = [
  'Checkpoints are visible on Apple Watch. Setting a new one requires iPhone, Mac or the web.',
  'iPhone and Mac display charts. Date filtering, hover values and series toggles are available on the web.',
  'Currently Mac only.',
  'During a session the Watch reports its own count, so the figure on your iPhone stays current even when the phone is stationary.',
  'Currently Mac and web.',
  'Reminders raised on iPhone are delivered to Apple Watch while the phone is locked.',
  'Apple Watch requires no separate sign-in. It displays data already held by the paired iPhone.',
  'Distance is measured from the file itself rather than taken from its metadata.',
  'Points added to shape the route are preserved, not only the named stops.',
  'Distance is taken from the route itself, so the figure and the line cannot diverge.',
  'Opens without an account. Shows position, distance and the map, and never pace, cadence or step counts. Lifetimes run from four hours to indefinite, and any link can be revoked.',
];

const HIGHLIGHTS: Array<{ title: string; body: string }> = [
  {
    title: 'Capture without effort',
    body: 'iPhone and Apple Watch record the distance between them. There is no session to '
      + 'start, nothing to remember, and no data lost when you walk out of range.',
  },
  {
    title: 'Progress you can measure',
    body: 'More than a step count: percentage complete, distance remaining, position against '
      + 'the daily target, and a projected finish date derived from your actual pace.',
  },
  {
    title: 'Visible everywhere',
    body: 'Home screen and Lock Screen widgets, watch face complications, a Mac menu bar item '
      + 'and a browser dashboard. The figure is in front of you before you go looking for it.',
  },
  {
    title: 'Mapped end to end',
    body: 'Plot a route between any two locations, or import one from a file or a Google Maps '
      + 'link. Track your position along it, and view the road ahead in Street View.',
  },
];

export function Features() {
  return (
    <main className="shell features">
      <a className="features__back" href="/">← Steps</a>

      <h1 className="features__title">Every mile, mapped to a real journey.</h1>
      <p className="features__lede">
        Steps records the distance you cover indoors and places it along a genuine route on the
        map. Plan the journey yourself or import one you already have, then track pace, targets
        and completion across iPhone, Apple Watch, Mac and the web.
      </p>

      <div className="features__highlights">
        {HIGHLIGHTS.map((item) => (
          <section key={item.title}>
            <h2>{item.title}</h2>
            <p>{item.body}</p>
          </section>
        ))}
      </div>

      <h2 className="features__h2">Feature availability by platform</h2>
      <p className="muted features__note">
        Four applications, one account. Capability differs by device by design: a watch face is
        built for a glance, and a browser cannot count steps.
      </p>

      <div className="features__scroll">
        <table className="matrix">
          <thead>
            <tr>
              <th scope="col">Feature</th>
              {PLATFORMS.map((platform) => <th key={platform} scope="col">{platform}</th>)}
            </tr>
          </thead>
          <tbody>
            {GROUPS.map((group) => (
              <Fragment key={group.title}>
                <tr>
                  <th className="matrix__group" scope="rowgroup" colSpan={5}>{group.title}</th>
                </tr>
                {group.features.map((feature) => (
                  <tr key={feature.name}>
                    <th scope="row">
                      {feature.name}
                      {feature.note && <sup className="matrix__fn">{feature.note}</sup>}
                    </th>
                    {feature.on.map((state, index) => (
                      <td key={PLATFORMS[index]}>
                        <span
                          className={`matrix__mark matrix__mark--${state}`}
                          aria-label={LABEL[state]}
                        >
                          {MARK[state]}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="features__legend">
        <span><span className="matrix__mark matrix__mark--full">●</span> Yes</span>
        <span><span className="matrix__mark matrix__mark--part">◐</span> Partly</span>
        <span><span className="matrix__mark matrix__mark--none">–</span> Not available</span>
      </p>

      <ol className="features__notes">
        {NOTES.map((note, index) => <li key={index}>{note}</li>)}
      </ol>

      {GROUPS.map((group) => (
        <section key={group.title} className="features__group">
          <h3>{group.title}</h3>
          <p>{group.blurb}</p>
        </section>
      ))}

      <p className="muted features__foot">
        Designed and built around a single journey, and refined against it.
      </p>
    </main>
  );
}
